"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.MAX_URL_FILTER_QUERY_LENGTH = void 0;
exports.escapePipeInValue = escapePipeInValue;
exports.unescapePipeInValue = unescapePipeInValue;
exports.splitOnUnescapedPipe = splitOnUnescapedPipe;
exports.computeSelectedValues = computeSelectedValues;
exports.encodeFiltersGeneric = encodeFiltersGeneric;
exports.decodeFiltersGeneric = decodeFiltersGeneric;
const filters_1 = require("../../interfaces/filters");
const sessionPositionInTrace_1 = require("./sessionPositionInTrace");
const encodeDelimitedArray = (values, delimiter) => values.join(delimiter);
const decodeDelimitedArray = (value, delimiter) => {
    if (value === "") {
        return [];
    }
    return value.split(delimiter);
};
// Escape pipe characters in values to avoid conflicts with the delimiter
// Uses backslash escaping: | → \|, and \ → \\
function escapePipeInValue(value) {
    return value.replace(/\\/g, "\\\\").replace(/\|/g, "\\|");
}
function unescapePipeInValue(value) {
    return value.replace(/\\\|/g, "|").replace(/\\\\/g, "\\");
}
// Split on unescaped pipe characters only (pipes not preceded by backslash)
function splitOnUnescapedPipe(str) {
    const result = [];
    let current = "";
    let i = 0;
    while (i < str.length) {
        if (str[i] === "\\" && i + 1 < str.length) {
            // Escaped character - include both backslash and next char
            current += str[i] + str[i + 1];
            i += 2;
        }
        else if (str[i] === "|") {
            // Unescaped pipe - split here
            result.push(current);
            current = "";
            i++;
        }
        else {
            current += str[i];
            i++;
        }
    }
    result.push(current);
    return result;
}
// Pure helper: compute UI-selected values from a filter entry and available values
// Checkboxes always show the KEPT set: "no filter" renders every option
// checked, and a `none of [excluded]` filter renders as everything-but-excluded
// checked — for stringOptions and arrayOptions alike (LFE-10717). This makes
// the deselect gesture round-trip: unchecking a value from the implicit-all
// state persists `none of [value]`, which displays as exactly that value
// unchecked.
function computeSelectedValues(availableValues, filterEntry) {
    if (!filterEntry)
        return availableValues;
    const values = filterEntry.value ?? [];
    if (filterEntry.operator === "none of") {
        const excluded = new Set(values);
        return availableValues.filter((v) => !excluded.has(v));
    }
    return values;
}
/**
 * Upper bound for a serialized `?filter=` value that may be written to the
 * URL. Browsers tolerate very long URLs, but the full request head (URL line
 * + cookies + headers) is capped at ~16KB by Node's HTTP server and most
 * proxies — an oversized filter query 431s the refresh/share round-trip it
 * exists for (LFE-10717). States above this budget are kept in the
 * session-storage mirror only: same-tab refreshes keep working, a copied link
 * degrades to "no filter" instead of a dead page.
 */
exports.MAX_URL_FILTER_QUERY_LENGTH = 4000;
/**
 * Encodes FilterState to the legacy semicolon-delimited format
 * Format: columnId;type;key;operator;value
 * Multiple filters separated by commas
 * Array values joined with |
 */
function encodeFiltersGeneric(filters) {
    return (encodeDelimitedArray(filters
        .map((f) => {
        // Determine the key field (for categoryOptions, numberObject, stringObject)
        const key = f.type === "numberObject" ||
            f.type === "stringObject" ||
            f.type === "booleanObject" ||
            f.type === "categoryOptions" ||
            f.type === "positionInTrace"
            ? f.key || ""
            : "";
        // Encode the value
        let encodedValue;
        if (f.type === "datetime") {
            encodedValue = encodeURIComponent(new Date(f.value).toISOString());
        }
        else if (f.type === "stringOptions" ||
            f.type === "arrayOptions" ||
            f.type === "categoryOptions") {
            // Escape pipe characters in individual values before joining with pipe delimiter
            const escapedValues = f.value.map(escapePipeInValue);
            encodedValue = encodeURIComponent(escapedValues.join("|"));
        }
        else if (f.type === "positionInTrace") {
            encodedValue =
                f.value === undefined || f.value === null
                    ? ""
                    : encodeURIComponent(String(f.value));
        }
        else {
            encodedValue = encodeURIComponent(String(f.value));
        }
        return `${f.column};${f.type};${key};${f.operator};${encodedValue}`;
    })
        .filter((s) => s !== null), ",") || "");
}
/**
 * Decodes the legacy semicolon-delimited format to FilterState
 * Format: column;type;key;operator;value
 */
function decodeFiltersGeneric(query) {
    if (!query.trim())
        return [];
    const decoded = decodeDelimitedArray(query, ",");
    if (!decoded)
        return [];
    const filters = [];
    for (const filterString of decoded) {
        if (!filterString)
            continue;
        const [column, type, key, operator, encodedValue] = filterString.split(";");
        if (!column || !type || !operator || encodedValue === undefined) {
            continue;
        }
        const decodedOperator = decodeURIComponent(operator);
        const decodedKey = key ? decodeURIComponent(key) : "";
        const normalizedKey = type === "positionInTrace"
            ? (0, sessionPositionInTrace_1.normalizeLegacySessionPositionInTraceKey)(decodedKey)
            : decodedKey;
        const decodedValue = decodeURIComponent(encodedValue);
        // Parse value based on type
        let parsedValue;
        if (type === "datetime") {
            parsedValue = new Date(decodedValue);
        }
        else if (type === "number" || type === "numberObject") {
            parsedValue = Number(decodedValue);
        }
        else if (type === "positionInTrace") {
            parsedValue = decodedValue === "" ? undefined : Number(decodedValue);
        }
        else if (type === "stringOptions" ||
            type === "arrayOptions" ||
            type === "categoryOptions") {
            // Split on unescaped pipe characters only, then unescape each value
            parsedValue = decodedValue
                ? splitOnUnescapedPipe(decodedValue).map(unescapePipeInValue)
                : type === "arrayOptions"
                    ? [] // Empty array for arrayOptions — empty strings are not valid array values (e.g., tags)
                    : decodedValue === ""
                        ? [""] // allow empty strings for stringOptions (i.e, filter for empty trace name)
                        : [decodedValue];
        }
        else if (type === "boolean" || type === "booleanObject") {
            parsedValue = decodedValue === "true";
        }
        else {
            parsedValue = decodedValue;
        }
        // Build filter object
        const filter = {
            column,
            type,
            operator: decodedOperator,
            value: parsedValue,
        };
        // Add key field for types that need it
        if (normalizedKey) {
            if (type === "categoryOptions" ||
                type === "numberObject" ||
                type === "booleanObject" ||
                type === "stringObject" ||
                type === "positionInTrace") {
                filter.key = normalizedKey;
            }
        }
        // Validate with zod
        const parsed = filters_1.singleFilter.safeParse(filter);
        if (parsed.success) {
            filters.push(parsed.data);
        }
        else {
            console.warn("Invalid filter skipped:", filter, parsed.error);
        }
    }
    return filters;
}
